import { useCallback, useMemo, useRef, useState } from "react";

import {
  encode,
  patternToTimeline,
  play,
  type Segment,
} from "@nav-games/morse";

import { BroadcastToggle } from "../../ui/BroadcastToggle";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface TranslateProps {
  onNavigate: (to: string) => void;
}

const PLAYBACK_WPM = 12;

export function Translate({ onNavigate }: TranslateProps) {
  const [text, setText] = useState("");
  const [hidden, setHidden] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Compute the dot/dash string + the audio/light timeline once
  // per text change. Cheap; no need to memoise across renders.
  const morse = useMemo(() => encode(text), [text]);
  const timeline = useMemo<readonly Segment[]>(
    () => patternToTimeline(morse, PLAYBACK_WPM),
    [morse],
  );

  const audioCtxRef = useRef<AudioContext | null>(null);
  const playAudio = useCallback(() => {
    if (timeline.length === 0) return;
    if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
    play(timeline, { audioContext: audioCtxRef.current });
  }, [timeline]);

  const [lit, setLit] = useState(false);
  const playLight = useCallback(() => {
    if (timeline.length === 0) return;
    let cancelled = false;
    let handle = 0;
    const step = (i: number) => {
      if (cancelled || i >= timeline.length) {
        setLit(false);
        return;
      }
      const seg = timeline[i]!;
      setLit(seg.on);
      handle = window.setTimeout(() => step(i + 1), seg.durationMs);
    };
    step(0);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
      setLit(false);
    };
  }, [timeline]);

  const rendered = morse.replace(/\./g, "•").replace(/-/g, "—");

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <h2 style={{ margin: 0 }}>Translate</h2>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "morse" : null} onClose={() => setHelpOpen(false)} />
      <p className="muted">
        Type a message — the Morse appears as you type. Play it as a tone or
        as a flashing light. Hit "Hide message" to cover the typed text and
        show only the signal — hand the phone to someone else to decode.
      </p>

      {!hidden && (
        <div className="card">
          <label className="small muted" htmlFor="msg">
            Text
          </label>
          <textarea
            id="msg"
            value={text}
            onChange={(e) => setText(e.target.value.toUpperCase())}
            placeholder="ANCHOR DOWN"
            rows={2}
            style={{
              width: "100%",
              marginTop: 4,
              padding: 8,
              fontFamily: "inherit",
              fontSize: 16,
              boxSizing: "border-box",
            }}
          />
          <button
            className="secondary"
            onClick={() => setHidden(true)}
            disabled={text.length === 0}
            style={{ marginTop: 8 }}
          >
            🙈 Hide message
          </button>
        </div>
      )}

      <div className="card" style={{ textAlign: "center" }}>
        {hidden && (
          <button
            className="secondary"
            onClick={() => setHidden(false)}
            style={{ marginBottom: 12 }}
          >
            👁 Reveal message
          </button>
        )}
        <div
          style={{
            fontFamily: "ui-monospace, monospace",
            fontSize: 22,
            letterSpacing: "0.1em",
            margin: "12px 0 16px",
            minHeight: "1.4em",
            wordBreak: "break-all",
          }}
        >
          {rendered || <span className="muted">—</span>}
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <button onClick={playAudio} disabled={timeline.length === 0}>
            ▶ Play tone
          </button>
          <button
            className="secondary"
            onClick={playLight}
            disabled={timeline.length === 0}
          >
            💡 Play flashes
          </button>
        </div>
        <div
          style={{
            margin: "16px auto 0",
            width: 96,
            height: 96,
            borderRadius: "50%",
            background: lit ? "#ffd84d" : "#1b2230",
            border: "3px solid #3a4854",
            boxShadow: lit ? "0 0 32px 8px rgba(255, 216, 77, 0.7)" : "none",
            transition: "background 30ms linear, box-shadow 30ms linear",
          }}
        />
      </div>

      <BroadcastToggle module="morse" text={text} />

      <button className="secondary" onClick={() => onNavigate("/play/morse")}>
        ← Back to Morse
      </button>
    </>
  );
}
